/**
 * Industries Testimonials Component
 * 
 * This component displays client testimonials for various industries in a carousel format.
 * Enhanced with premium design elements, animations, and loading states.
 */
"use client";

import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { motion, AnimatePresence, useAnimation, PanInfo, useReducedMotion, type Variants } from "framer-motion";
import { Badge } from '@/components/ui/design-system';
import Image from "next/image";
import { 
  Heading, 
  Text, 
  Button, 
  Card, 
  CardContent,
  LoadingSpinner,
  containerVariants, 
  itemVariants, 
  fadeInVariants 
} from "@/components/ui/design-system";
import { ArrowRight, ArrowLeft, Star, Quote } from "lucide-react";
import { supabaseAdapter } from '@/adapters/supabase';

const MotionBadge = motion(Badge);

// Design tokens and constants

const BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
};

const DISPLAY_COUNTS = {
  mobile: 1,
  tablet: 2,
  desktop: 3,
};

const ANIMATION_SETTINGS = {
  spring: {
    stiffness: 300,
    damping: 30,
    mass: 1,
  },
  duration: {
    short: 0.2,
    normal: 0.3,
    medium: 0.4,
    long: 0.6,
  },
  stagger: {
    default: 0.1,
    fast: 0.06,
    slow: 0.15,
  }
};

// Testimonial data structure
interface Testimonial {
  id: string;
  quote: string;
  name: string;
  title: string;
  company?: string;
  location?: string;
  imageSrc: string;
  rating: number;
  accentColor: string;
}

// Allowed accent color keys used by colorMappings
const ACCENT_KEYS = ["primary", "amber", "green", "blue"] as const;
type AccentKey = typeof ACCENT_KEYS[number];

// Normalize accent color from DB to the closest valid palette key
function normalizeAccent(input: unknown): AccentKey {
  const val = String(input || "").toLowerCase().trim();
  return (ACCENT_KEYS as readonly string[]).includes(val) ? (val as AccentKey) : "primary";
}

// Clamp rating safely to 1..5
function clampRating(input: unknown): number {
  const n = typeof input === 'number' ? input : Number(input);
  if (!Number.isFinite(n)) return 5;
  return Math.min(5, Math.max(1, Math.round(n)));
}

// No hardcoded testimonials; data is loaded from Supabase.

// Color mapping configuration
const colorMappings = {
  accent: {
    primary: "bg-primary-500 dark:bg-primary-500",
    amber: "bg-amber-500 dark:bg-amber-500",
    green: "bg-green-500 dark:bg-green-500",
    blue: "bg-blue-500 dark:bg-blue-500",
  },
  text: {
    primary: "text-primary-500 dark:text-primary-500",
    amber: "text-amber-500 dark:text-amber-500",
    green: "text-green-500 dark:text-green-500",
    blue: "text-blue-500 dark:text-blue-500",
  },
  border: {
    primary: "border-primary-500 dark:border-primary-500",
    amber: "border-amber-500 dark:border-amber-500",
    green: "border-green-500 dark:border-green-500",
    blue: "border-blue-500 dark:border-blue-500",
  },
  background: {
    primary: "bg-primary-100 dark:bg-primary-900/40",
    amber: "bg-amber-100 dark:bg-amber-900/40",
    green: "bg-green-100 dark:bg-green-900/40",
    blue: "bg-blue-100 dark:bg-blue-900/40",
  },
};

// Skeleton loader for testimonial cards
function TestimonialSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-full bg-gray-100 dark:bg-gray-800 rounded-xl overflow-hidden border-0 shadow-md">
        <div className="h-2 w-full bg-gray-200 dark:bg-gray-700"></div>
        <div className="p-8 relative">
          <div className="absolute -top-6 left-8 w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
          <div className="flex mb-4 pt-2 space-x-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-4 w-4 bg-gray-200 dark:bg-gray-700"></div>
            ))}
          </div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 mb-2 w-3/4"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 mb-2"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 mb-2 w-5/6"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 mb-6 w-2/3"></div>
          
          <div className="flex items-center">
            <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-700 mr-4"></div>
            <div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 w-24 mb-2"></div>
              <div className="h-3 bg-gray-200 dark:bg-gray-700 w-32"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Testimonial Card Component
function TestimonialCard({ testimonial, index }: { testimonial: Testimonial, index: number, key?: string }) {
  // Get the appropriate classes based on accent color
  const accentColorClass = colorMappings.accent[testimonial.accentColor as keyof typeof colorMappings.accent] || colorMappings.accent.primary;
  const textColorClass = colorMappings.text[testimonial.accentColor as keyof typeof colorMappings.text] || colorMappings.text.primary;
  const borderColorClass = colorMappings.border[testimonial.accentColor as keyof typeof colorMappings.border] || colorMappings.border.primary;
  const bgColorClass = colorMappings.background[testimonial.accentColor as keyof typeof colorMappings.background] || colorMappings.background.primary;
  
  // Generate star rating with enhanced animations
  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <motion.div 
        key={`star-${i}-${testimonial.id}`}
        initial={{ opacity: 0, scale: 0.5, y: -10 }}
        animate={{ 
          opacity: 1, 
          scale: 1,
          y: 0
        }}
        transition={{ 
          delay: 0.2 + (i * 0.1),
          duration: 0.4,
          type: "spring",
          stiffness: 200,
          damping: 15
        }}
      >
        <motion.div
          animate={{
            rotate: [0, i % 2 === 0 ? 10 : -10, 0]
          }}
          transition={{
            duration: 0.8,
            ease: "easeInOut",
            delay: 0.3 + (i * 0.1),
            times: [0, 0.5, 1]
          }}
        >
          <Star 
            className={`h-4 w-4 ${i < rating ? "text-amber-400 fill-amber-400" : "text-gray-300 dark:text-gray-600"}`} 
          />
        </motion.div>
      </motion.div>
    ));
  };
  
  return (
    <motion.div 
      variants={itemVariants} 
      custom={index}
      whileHover={{ 
        y: -8,
        transition: { 
          type: "spring", 
          stiffness: ANIMATION_SETTINGS.spring.stiffness, 
          damping: ANIMATION_SETTINGS.spring.damping 
        }
      }}
      whileTap={{ scale: 0.98 }}
      className="h-full perspective-1000"
    >
      <Card 
        className="h-full shadow-lgxl rounded-xl overflow-visible border-0 relative z-10 bg-white dark:bg-gray-800 mx-2 my-4"
        elevation="lg"
        hover="lift"
      >
        {/* Card top accent bar with animation */}
        <motion.div 
          className={`h-2 w-full ${accentColorClass}`}
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ 
            delay: 0.1, 
            duration: ANIMATION_SETTINGS.duration.medium,
            ease: "easeOut"
          }}
        ></motion.div>
        
        <CardContent className="p-8 relative">
          {/* Quote icon with enhanced animation */}
          <motion.div 
            className={`absolute -top-6 left-8 w-11 h-11 flex items-center justify-center bg-white dark:bg-gray-800 rounded-full border-2 ${borderColorClass} shadow-md`}
            initial={{ scale: 0, rotate: -180, opacity: 0 }}
            animate={{ 
              scale: 1, 
              rotate: 0,
              opacity: 1
            }}
            transition={{ 
              type: "spring", 
              stiffness: 260, 
              damping: 20,
              delay: 0.2,
            }}
          >
            <Quote className={`h-6 w-6 ${textColorClass}`} />
          </motion.div>
          
          {/* Rating with enhanced animation */}
          <div className="flex mb-4 pt-2 gap-1">
            {renderStars(testimonial.rating)}
          </div>
          
          {/* Quote with enhanced staggered character animation */}
          <div className="text-gray-600 dark:text-gray-200 mb-6 italic min-h-[80px] line-clamp-4">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{
                hidden: {},
                visible: {
                  transition: {
                    staggerChildren: 0.015,
                  }
                }
              }}
            >
              {testimonial.quote.split('').map((char, index) => (
                <motion.span
                  key={`char-${index}-${testimonial.id}`}
                  variants={{
                    hidden: { opacity: 0, y: 10 },
                    visible: { 
                      opacity: 1, 
                      y: 0,
                      transition: {
                        type: "spring",
                        damping: 12,
                        stiffness: 100,
                      }
                    }
                  }}
                >
                  {char}
                </motion.span>
              ))}
            </motion.div>
          </div>
          
          {/* Profile with enhanced animations */}
          <motion.div 
            className="flex items-center mt-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              delay: 0.4,
              duration: ANIMATION_SETTINGS.duration.medium,
              ease: "easeOut"
            }}
          >
            <motion.div 
              className={`w-12 h-12 rounded-full overflow-hidden mr-4 shadow-lgmd border-2 border-white dark:border-gray-700 ${bgColorClass} flex-shrink-0`}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ 
                scale: 1,
                opacity: 1,
              }}
              transition={{
                delay: 0.5,
                type: "spring",
                stiffness: 200,
                damping: 15
              }}
              whileHover={{ 
                scale: 1.1,
                boxShadow: "0 0 20px rgba(255, 82, 82, 0.4)",
                transition: { duration: 0.2 }
              }}
            >
              <Image 
                src={testimonial.imageSrc} 
                alt={testimonial.name} 
                width={48} 
                height={48} 
                className="object-cover w-full h-full"
                priority
              />
            </motion.div>
            <div>
              <motion.div 
                className="font-semibold text-gray-900 dark:text-white"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5, duration: 0.3 }}
              >
                {testimonial.name}
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: -5 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6, duration: 0.3 }}
              >
                <Text variant="caption" className="text-sm text-gray-500 dark:text-gray-300">
                  {testimonial.title}
                  {testimonial.company && `, ${testimonial.company}`}
                  {testimonial.location && `, ${testimonial.location}`}
                </Text>
              </motion.div>
            </div>
          </motion.div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// Pagination dot component
function PaginationDot({ 
  isActive, 
  onClick 
}: { 
  isActive: boolean, 
  onClick: () => void 
}) {
  const classes = `transition-all duration-300 rounded-full ${
    isActive 
      ? `bg-primary-500 dark:bg-primary-500 w-12 h-3 shadow-md dark:shadow-md hover:shadow-lg dark:hover:shadow-lg` 
      : "bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 w-3 h-3 hover:w-6 dark:hover:w-6"
  }`;
  
  return (
    <button
      onClick={onClick}
      className={classes}
      aria-label={isActive ? 'Current page' : 'Go to page'}
    />
  );
}

export default function IndustriesTestimonials() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [displayCount, setDisplayCount] = useState(DISPLAY_COUNTS.desktop);
  const [direction, setDirection] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  const [isTabletPortrait, setIsTabletPortrait] = useState(false);
  const [animationKey, setAnimationKey] = useState(0); // Add key to force animations to replay
  const controls = useAnimation();
  const carouselRef = useRef<HTMLDivElement>(null);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const reduceMotion = useReducedMotion();
  
  // Responsive display counts
  const getDisplayCount = () => {
    if (typeof window === 'undefined') return DISPLAY_COUNTS.desktop; // SSR default
    
    if (window.innerWidth < BREAKPOINTS.mobile) return DISPLAY_COUNTS.mobile;
    if (window.innerWidth < BREAKPOINTS.tablet) return DISPLAY_COUNTS.tablet;
    return DISPLAY_COUNTS.desktop;
  };
  
  // Check if device is touch-enabled
  const checkIsTouchDevice = () => {
    if (typeof window === 'undefined') return false;
    return (
      isMobile || 
      isTablet || 
      ('ontouchstart' in window) || 
      (navigator.maxTouchPoints > 0) ||
      // @ts-ignore - Some browsers use msMaxTouchPoints
      (navigator.msMaxTouchPoints > 0)
    );
  };
  
  // Update display count and device type on resize or orientation change
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      setDisplayCount(getDisplayCount());
      setIsMobile(width < BREAKPOINTS.mobile);
      setIsTablet(width >= BREAKPOINTS.mobile && width < BREAKPOINTS.tablet);
      setIsLandscape(width > height);
      setIsTouchDevice(checkIsTouchDevice());
      
      // Check if device is a tablet in portrait mode
      setIsTabletPortrait(width >= BREAKPOINTS.mobile && width < BREAKPOINTS.tablet && width <= height);
    };
    
    // Set initial values
    handleResize();
    
    // Add event listeners
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);
  
  // Load testimonials from Supabase
  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        const { data, error } = await supabaseAdapter.client
          .from('testimonials')
          .select('id, name, company, designation, content, image_url, location, rating, accent_color, deleted_at')
          .is('deleted_at', null)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Failed to load testimonials:', error);
          // Keep empty state; do not use hardcoded data
        } else {
          const mapped: Testimonial[] = (data || []).map((t: any) => ({
            id: String(t.id),
            quote: t.content,
            name: t.name,
            title: t.designation || '',
            company: t.company || undefined,
            location: t.location || undefined,
            imageSrc: t.image_url || '/images/pages/industries/testimonials/testimonial-1.jpg',
            rating: clampRating(t.rating),
            accentColor: normalizeAccent(t.accent_color),
          }));
          if (isMounted) setTestimonials(mapped);
        }
      } catch (e) {
        console.error('Unexpected error loading testimonials:', e);
        // Keep empty state
      } finally {
        if (isMounted) {
          setIsLoading(false);
          setAnimationKey(prev => prev + 1);
        }
      }
    };
    load();
    return () => { isMounted = false; };
  }, []);
  
  const totalTestimonials = testimonials.length;
  
  // Determine if at first or last card
  const isFirstCard = currentIndex === 0;
  const isLastCard = currentIndex >= totalTestimonials - displayCount;
  
  // Get current testimonials to display - show displayCount cards starting from currentIndex
  const currentTestimonials = useMemo(() => {
    return testimonials.slice(
      currentIndex,
      Math.min(currentIndex + displayCount, totalTestimonials)
    );
  }, [testimonials, currentIndex, displayCount, totalTestimonials]);
  
  // Calculate current page for pagination dots
  const currentPage = useMemo(() => Math.floor(currentIndex / displayCount), [currentIndex, displayCount]);
  const totalPages = useMemo(() => Math.ceil(totalTestimonials / displayCount), [totalTestimonials, displayCount]);
  
  // Smoother navigation handlers with optimized transition timing
  const handlePrevious = useCallback(() => {
    if (!isFirstCard && !isTransitioning) {
      setIsTransitioning(true);
      setDirection(-1);
      // Increment animation key to force animations to replay
      setAnimationKey(prev => prev + 1);
      // Move back by ONE card instead of displayCount
      setCurrentIndex(Math.max(0, currentIndex - 1));
      
      // Reset transition state after animation completes
      setTimeout(() => setIsTransitioning(false), 350);
    }
  }, [isFirstCard, isTransitioning, currentIndex]);
  
  const handleNext = useCallback(() => {
    if (!isTransitioning) {
      setIsTransitioning(true);
      setDirection(1);
      // Increment animation key to force animations to replay
      setAnimationKey(prev => prev + 1);
      
      if (!isLastCard) {
        // Move forward by ONE card instead of displayCount
        setCurrentIndex(Math.min(totalTestimonials - displayCount, currentIndex + 1));
      } else {
        // Loop to the beginning if at the end
        setCurrentIndex(0);
      }
      
      // Reset transition state after animation completes
      setTimeout(() => setIsTransitioning(false), 350);
    }
  }, [isTransitioning, isLastCard, totalTestimonials, displayCount, currentIndex]);
  
  // Handle direct pagination navigation
  const goToPage = useCallback((pageIndex: number) => {
    if (!isTransitioning && pageIndex !== currentPage) {
      setIsTransitioning(true);
      setDirection(pageIndex > currentPage ? 1 : -1);
      // Increment animation key to force animations to replay
      setAnimationKey(prev => prev + 1);
      // Go to the first card of the selected page
      setCurrentIndex(pageIndex * displayCount);
      
      // Reset transition state after animation completes
      setTimeout(() => setIsTransitioning(false), 350);
    }
  }, [isTransitioning, currentPage, displayCount]);
  
  // Handle swipe gestures with improved detection
  const handleDragEnd = useCallback((event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (isTransitioning) return;
    
    const swipeThreshold = 30; // Lower threshold for easier activation
    const { offset, velocity } = info;
    
    // Consider both distance and velocity for better swipe detection
    if (offset.x > swipeThreshold || velocity.x > 0.5) {
      // Increment animation key to force animations to replay
      setAnimationKey(prev => prev + 1);
      handlePrevious();
    } else if (offset.x < -swipeThreshold || velocity.x < -0.5) {
      // Increment animation key to force animations to replay
      setAnimationKey(prev => prev + 1);
      handleNext();
    } else {
      // If the swipe wasn't strong enough, animate back to center with a gentle spring
      controls.start({ 
        x: 0,
        transition: { type: "spring", stiffness: 300, damping: 25 }
      });
    }
  }, [isTransitioning, controls, handleNext, handlePrevious]);
  
  // Optimized card transition variants for smoother animations
    const cardVariants: Variants = {
    initial: (direction: number) => ({
      x: reduceMotion ? 0 : (direction > 0 ? 40 : -40),
      opacity: reduceMotion ? 1 : 0,
      scale: reduceMotion ? 1 : 0.95,
    }),
    animate: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: {
        x: reduceMotion ? undefined : { type: "spring", stiffness: 250, damping: 25, mass: 0.8 },
        opacity: { duration: 0.35, ease: "easeOut" },
        scale: { duration: 0.35, ease: "easeOut" },
      }
    },
    exit: (direction: number) => ({
      x: reduceMotion ? 0 : (direction < 0 ? 40 : -40),
      opacity: reduceMotion ? 1 : 0,
      scale: reduceMotion ? 1 : 0.95,
      transition: {
        x: reduceMotion ? undefined : { type: "spring", stiffness: 250, damping: 25, mass: 0.8 },
        opacity: { duration: 0.25, ease: "easeIn" },
        scale: { duration: 0.25, ease: "easeIn" },
      }
    })
  };

  return (
    <section 
    data-theme-section="dark"
    role="region"
    aria-labelledby="industries-testimonials-heading"
    className="py-16 sm:py-24 bg-gradient-to-b from-gray-100 to-white dark:from-gray-900 dark:to-gray-950 relative overflow-hidden">
      {/* Background elements with enhanced animations */}
      <div className="absolute inset-0 overflow-hidden -z-10">
        <motion.div 
          className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gradient-to-tl from-primary-100/20 to-transparent dark:from-primary-900/20 rounded-full blur-3xl transform-gpu"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ 
            opacity: 1, 
            scale: 1
          }}
          transition={{ 
            delay: 0.3,
            duration: 0.6
          }}
        ></motion.div>
        <motion.div 
          className="absolute top-0 left-0 w-[300px] h-[300px] bg-gradient-to-br from-amber-100/10 to-transparent dark:from-amber-900/10 rounded-full blur-2xl transform-gpu"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ 
            opacity: 1, 
            scale: 1
          }}
          transition={{ 
            delay: 0.6,
            duration: 0.6
          }}
        ></motion.div>
        
        {/* Background animations using keyframes/tween instead of spring */}
        <motion.div
          className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gradient-to-tl from-primary-100/20 to-transparent dark:from-primary-900/30 rounded-full blur-3xl transform-gpu opacity-50"
          initial={{ opacity: 0.3 }}
          animate={{
            scale: [1, 1.05, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "easeInOut",
            times: [0, 0.5, 1]
          }}
        ></motion.div>
        <motion.div
          className="absolute top-0 left-0 w-[300px] h-[300px] bg-gradient-to-br from-amber-100/10 to-transparent dark:from-amber-900/15 rounded-full blur-2xl transform-gpu opacity-50"
          initial={{ opacity: 0.2 }}
          animate={{
            scale: [1, 1.1, 1],
            x: [0, 20, 0],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "easeInOut",
            times: [0, 0.5, 1]
          }}
        ></motion.div>
        
        {/* Additional animated background elements */}
        <motion.div 
          className="absolute top-1/4 right-1/4 w-[200px] h-[200px] bg-gradient-to-br from-blue-100/10 to-transparent dark:from-blue-900/5 rounded-full blur-xl transform-gpu"
          initial={{ opacity: 0 }}
          animate={{
            opacity: [0, 0.3, 0],
            scale: [0.8, 1, 0.8],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            repeatType: "reverse",
            delay: 1.5,
            ease: "easeInOut",
            times: [0, 0.5, 1]
          }}
        ></motion.div>
      </div>
      
      <div className="container mx-auto px-4 sm:px-4 md:px-6 lg:px-8">
        <motion.div 
          className="max-w-6xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
        >
          {/* Header with enhanced animations */}
          <motion.div variants={itemVariants} className="text-center mb-8">
            <MotionBadge
              variant="premium"
              size="lg"
              radius="full"
              className="mb-2" // Keep existing margin
              initial={{ opacity: 0, y: -20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{
                type: "spring",
                stiffness: 500,
                damping: 25,
                delay: 0.1
              }}
            >
              Client Success Stories
            </MotionBadge>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                delay: 0.3,
                type: "spring", 
                stiffness: 100, 
                damping: 20 
              }}
            >
              <Heading id="industries-testimonials-heading" level={2} className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 dark:text-white">
                What Our Clients Say
              </Heading>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                delay: 0.5, 
                type: "spring", 
                stiffness: 100, 
                damping: 20 
              }}
            >
              <Text variant="body" className="text-gray-600 dark:text-gray-300 text-lg max-w-3xl mx-auto">
                Hear from our satisfied clients across various industries about their experience with Pavisha packaging solutions.
              </Text>
            </motion.div>
          </motion.div>
          
          {/* Testimonial carousel with improved transitions */}
          <div className="relative px-4 py-4 overflow-visible" ref={carouselRef}>
            <motion.div 
              className="overflow-visible"
              animate={{ 
                y: isTransitioning ? 1 : 0
              }}
              transition={{ 
                duration: 0.2, 
                ease: "easeInOut"
              }}
            >
              <motion.div 
                drag={isTouchDevice ? "x" : false}
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.1}
                onDragEnd={handleDragEnd}
                animate={controls}
                className={`${isTouchDevice ? "touch-pan-y cursor-grab active:cursor-grabbing" : ""}`}
              >
                {/* Use grid for layout but AnimatePresence on individual cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                  {isLoading ? (
                    // Skeleton loaders while loading
                    Array.from({ length: displayCount }).map((_, index) => (
                      <TestimonialSkeleton key={`skeleton-${index}`} />
                    ))
                  ) : (
                    // Actual testimonial cards with animations forced to replay on each navigation
                    currentTestimonials.map((testimonial, index) => (
                      <AnimatePresence mode="popLayout" key={`${testimonial.id}-${animationKey}`} initial={true}>
                        <motion.div
                          custom={direction}
                          variants={cardVariants}
                          initial="initial"
                          animate="animate"
                          exit="exit"
                          key={`${testimonial.id}-${animationKey}`}
                          layout="position"
                        >
                          <TestimonialCard 
                            testimonial={testimonial} 
                            index={index + currentIndex}
                            key={`card-${testimonial.id}-${animationKey}`}
                          />
                        </motion.div>
                      </AnimatePresence>
                    ))
                  )}
                </div>
              </motion.div>
            </motion.div>
          </div>
          
          {/* Button and controls wrapper with fixed height to prevent movement */}
          <div className="h-16 relative"> {/* Fixed-height container for buttons */}
            {/* Enhanced pagination dots with animations - hidden in tablet portrait and mobile */}
            <motion.div 
              className={`mt-6 mb-4 ${isMobile || isTabletPortrait ? 'hidden' : 'block'}`}
              variants={itemVariants}
            >
              <motion.div 
                variants={itemVariants}
                className="flex justify-center md:justify-start mb-6"
              >
                <div className="flex items-center space-x-6">
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <motion.div
                      key={i}
                      whileHover={{ scale: 1.2 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <PaginationDot 
                        isActive={currentPage === i}
                        onClick={() => goToPage(i)}
                      />
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
              
            {/* Navigation buttons - hidden in tablet portrait and mobile */}
            <motion.div 
              variants={itemVariants}
              className={`absolute top-0 right-0 flex justify-center md:justify-end ${isMobile || isTabletPortrait ? 'hidden' : 'block'}`}
            >
              <div className="flex space-x-4">
                <motion.div
                  whileHover={{ scale: 1.05, x: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    variant="outline"
                    size="md"
                    onClick={handlePrevious}
                    disabled={isFirstCard || isTransitioning}
                    aria-label="Previous testimonials"
                    startIcon={<ArrowLeft className="h-4 w-4" />}
                    className={isTransitioning ? "opacity-70 cursor-not-allowed" : ""}
                  >
                    Previous
                  </Button>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05, x: 2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    variant="primary"
                    size="md"
                    onClick={handleNext}
                    aria-label="Next testimonials"
                    endIcon={<ArrowRight className="h-4 w-4" />}
                    disabled={isTransitioning}
                    className={isTransitioning ? "opacity-70 cursor-not-allowed" : ""}
                  >
                    Next
                  </Button>
                </motion.div>
              </div>
            </motion.div>
          </div>
          
          {/* Touch device swipe indicator - show on mobile and tablet with increased spacing */}
          {isTouchDevice && !isLoading && (
            <motion.div 
              className="flex justify-center items-center mt-2 mb-1 text-gray-400 dark:text-gray-500 text-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              <span>Swipe to navigate</span>
              <ArrowRight className="h-4 w-4 ml-2" />
            </motion.div>
          )}
          
          {/* View more testimonials button with bounce animation - disabled */}
          <motion.div 
            variants={itemVariants} 
            className="flex justify-center mt-10"
          >
            <motion.div
              animate={{ 
                y: [0, -5, 0]
              }}
              transition={{ 
                repeat: Infinity, 
                repeatType: "reverse", 
                duration: 2,
                ease: "easeInOut",
                times: [0, 0.5, 1]
              }}
            >
              <Button 
                variant="secondary"
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                className="hover:cursor-no-drop select-none"
                aria-disabled="true"
                tabIndex={-1}
                endIcon={<ArrowRight className="h-4 w-4" />}
              >
                View All Testimonials
              </Button>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
      
      {/* Add a subtle motion effect to the entire section */}
      <motion.div 
        className="absolute inset-0 pointer-events-none"
        animate={{
          backgroundPosition: ['0% 0%', '100% 100%']
        }}
        transition={{
          repeat: Infinity,
          repeatType: "reverse",
          duration: 20,
        }}
      />
    </section>
  );
}